import React, { useState } from "react";
import { FaCheckCircle, FaTimesCircle, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import "katex/dist/katex.min.css";

// Result page opened from a "Recent Activity" row (student dashboard, or a
// teacher/parent viewing a student). Unlike /result-exam (which is hardcoded to
// go back to /exam-history), this page's Back button returns to wherever the
// viewer came from, passed in navigation state as `returnTo`:
//   - student dashboard -> "/dashboard"
//   - teacher           -> "/student-stats"
//   - parent            -> "/child-stats"
// The student stats page reads the student id from localStorage, so returning
// to that path re-loads the correct student.
const ResultActivityPage = () => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState("All");
    const navigate = useNavigate();
    const { state } = useLocation();
    const { result = [], returnTo, title } = state || {};

    // Subjects list
    const subjects = ["All", ...new Set(result.map((r) => r?.subject ?? "Unknown"))];

    // Filtered results
    const filteredResults =
        selectedSubject === "All"
            ? result
            : result.filter((r) => (r?.subject ?? "Unknown") === selectedSubject);

    const totalQuestions = filteredResults.length;
    const currentQuestion = filteredResults[currentQuestionIndex] || {};

    // Get normalized question object
    const questionData = currentQuestion?.question ?? {};

    // Convert any value safely to text
    const getText = (val) => {
        if (val == null) return "";
        if (typeof val === "string" || typeof val === "number") return String(val);
        return JSON.stringify(val);
    };

    // Normalize answers (map 1-4 to A-D)
    const normalizeAnswer = (ans) => {
        if (!ans) return "";
        const map = { "1": "A", "2": "B", "3": "C", "4": "D" };
        const str = ans.toString().trim().toUpperCase();
        return map[str] || str;
    };

    // Extract correct and selected
    const correctAnswer = normalizeAnswer(questionData.correctAnswer);
    const selectedAnswer = normalizeAnswer(currentQuestion.selected_option);

    // MathJax config
    const mathJaxConfig = {
        loader: { load: ["input/asciimath", "output/chtml"] },
        tex: {
            inlineMath: [["$", "$"], ["\\(", "\\)"]],
            displayMath: [["$$", "$$"], ["\\[", "\\]"]],
        },
    };

    // Detects any math delimiter, including single-dollar `$...$` inline math.
    const HAS_MATH = /\$|\\\(|\\\[/;

    // One renderer for questions, options and explanations. We split only on
    // **bold** and hand each remaining chunk to MathJax as-is, so MathJax itself
    // typesets every delimiter ($...$, $$...$$, \(...\), \[...\]) per the config.
    const renderRichText = (raw) => {
        const text = getText(raw);
        if (!text) return <span />;
        return text.split(/(\*\*[\s\S]*?\*\*)/g).map((part, i) => {
            if (!part) return null;
            if (part.length >= 4 && part.startsWith("**") && part.endsWith("**")) {
                return (
                    <strong key={i} className="font-semibold">
                        {part.slice(2, -2)}
                    </strong>
                );
            }
            if (HAS_MATH.test(part)) {
                return (
                    <MathJax key={i} inline dynamic>
                        {part}
                    </MathJax>
                );
            }
            return <span key={i}>{part}</span>;
        });
    };

    // Compute overall score
    const correctCount = filteredResults.reduce((acc, item) => {
        const q = item.question || {};
        const correct = normalizeAnswer(q.correctAnswer);
        const selected = normalizeAnswer(item.selected_option);
        return acc + (correct === selected ? 1 : 0);
    }, 0);

    const percentage =
        totalQuestions > 0 ? ((correctCount / totalQuestions) * 100).toFixed(1) : 0;

    // Navigation handlers
    const handleNext = () => {
        if (currentQuestionIndex < totalQuestions - 1) setCurrentQuestionIndex((p) => p + 1);
    };
    const handleBack = () => {
        if (currentQuestionIndex > 0) setCurrentQuestionIndex((p) => p - 1);
    };
    const toggleSidebar = () => setIsSidebarOpen((p) => !p);
    const handleSubjectChange = (subject) => {
        setSelectedSubject(subject);
        setCurrentQuestionIndex(0);
    };
    // Return to whoever opened this result; fall back to browser history.
    const goBack = () => {
        if (returnTo) navigate(returnTo);
        else navigate(-1);
    };

    // No results
    if (totalQuestions === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div className="bg-white shadow rounded p-6 text-center">
                    <h2 className="text-xl font-bold mb-2">No Results</h2>
                    <p className="text-gray-600">No results found to display.</p>
                    <div className="mt-4">
                        <button onClick={goBack} className="px-4 py-2 bg-[#2148C0] text-white rounded">
                            Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Build options
    const options = [
        { key: "A", text: getText(questionData.option_a) },
        { key: "B", text: getText(questionData.option_b) },
        { key: "C", text: getText(questionData.option_c) },
        { key: "D", text: getText(questionData.option_d) },
    ];

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="flex flex-col min-h-screen bg-gradient-to-r from-gray-100 to-gray-200">
                {/* Header */}
                <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-6">
                    <h1 className="text-2xl font-bold text-white">{title || "Exam Result"}</h1>
                    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-white text-sm sm:text-base">
                        <span className="px-2 py-1 bg-blue-700 rounded-md">
                            Total Questions: <strong>{totalQuestions}</strong>
                        </span>
                        <span className="px-2 py-1 bg-green-600 rounded-md">
                            Score: <strong>{correctCount}</strong>
                        </span>
                        <span
                            className={`px-2 py-1 rounded-md ${percentage >= 50 ? "bg-green-500" : "bg-red-500"
                                }`}
                        >
                            Percentage: <strong>{percentage}%</strong>
                        </span>
                    </div>
                </nav>

                {/* Subject Filter */}
                <div className="p-4 sm:p-6 bg-white shadow-md">
                    <div className="flex gap-4 overflow-x-auto">
                        {subjects.map((subject, idx) => (
                            <button
                                key={idx}
                                className={`px-4 py-2 rounded-lg font-semibold ${selectedSubject === subject ? "bg-blue-500 text-white" : "bg-gray-200"
                                    }`}
                                onClick={() => handleSubjectChange(subject)}
                            >
                                {subject}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Back button */}
                <div className="flex items-center mt-6 ml-3 mb-6">
                    <button
                        onClick={goBack}
                        className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition"
                    >
                        <FaArrowLeft className="text-xl" />
                        Back
                    </button>
                </div>

                {/* Main layout */}
                <div className="flex-grow flex flex-col-reverse sm:flex-row">
                    {/* Main content */}
                    <main className="flex-1 p-4 sm:p-6 bg-white shadow-lg rounded-lg mx-4 sm:mx-6 overflow-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg sm:text-xl font-bold text-[#2148C0]">
                                Question {currentQuestionIndex + 1} / {totalQuestions}
                            </h2>
                            <button
                                onClick={toggleSidebar}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 flex items-center transition"
                            >
                                {isSidebarOpen ? "Hide" : "Show"} Sidebar
                            </button>
                        </div>

                        <div className="result-rich bg-gray-50 p-4 sm:p-6 rounded-lg shadow-md">
                            {/* Keep long equations inside their box: wrap normal
                                text, and let wide inline math scroll horizontally. */}
                            <style>{`
                                .result-rich { overflow-wrap: anywhere; word-break: break-word; }
                                .result-rich mjx-container,
                                .result-rich .mjx-container { max-width: 100%; overflow-x: auto; overflow-y: hidden; }
                            `}</style>
                            <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
                                {renderRichText(questionData.question)}
                            </h3>

                            {/* Options */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {options.map((opt) => {
                                    if (!opt.text) return null;
                                    const letter = opt.key;
                                    const isCorrect = letter === correctAnswer;
                                    const isSelected = letter === selectedAnswer;

                                    return (
                                        <div
                                            key={letter}
                                            className={`p-4 rounded-lg shadow-md flex items-center justify-between gap-2 border ${isCorrect
                                                    ? "border-green-500 bg-green-50"
                                                    : isSelected
                                                        ? "border-red-500 bg-red-50"
                                                        : "border-gray-300"
                                                }`}
                                        >
                                            <span className="min-w-0 break-words">
                                                {letter}. {renderRichText(opt.text)}
                                            </span>
                                            {isCorrect && <FaCheckCircle className="shrink-0 text-green-500 text-xl" />}
                                            {isSelected && !isCorrect && (
                                                <FaTimesCircle className="shrink-0 text-red-500 text-xl" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* No selection */}
                            {!selectedAnswer && (
                                <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
                                    <h4 className="font-semibold text-yellow-600">Option Not Selected</h4>
                                    <p>This question was left unanswered.</p>
                                </div>
                            )}

                            {/* Explanation */}
                            <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg overflow-x-auto">
                                <h4 className="font-semibold text-[#2148C0]">Explanation:</h4>
                                <p className="break-words whitespace-pre-wrap">{renderRichText(questionData.explanation)}</p>
                            </div>
                        </div>

                        {/* Navigation */}
                        <div className="flex justify-between mt-6">
                            <button
                                onClick={handleBack}
                                className={`px-4 py-2 rounded-lg ${currentQuestionIndex > 0
                                        ? "bg-[#2148C0] text-white hover:bg-blue-600"
                                        : "bg-gray-300 text-gray-500"
                                    }`}
                                disabled={currentQuestionIndex === 0}
                            >
                                <FaArrowLeft className="inline mr-2" /> Back
                            </button>
                            <button
                                onClick={handleNext}
                                className={`px-4 py-2 rounded-lg ${currentQuestionIndex < totalQuestions - 1
                                        ? "bg-[#2148C0] text-white hover:bg-blue-600"
                                        : "bg-gray-300 text-gray-500"
                                    }`}
                                disabled={currentQuestionIndex === totalQuestions - 1}
                            >
                                Next <FaArrowRight className="inline ml-2" />
                            </button>
                        </div>
                    </main>

                    {/* Sidebar */}
                    {isSidebarOpen && (
                        <aside className="absolute sm:relative w-full sm:w-64 bg-white shadow-lg p-4 border-l z-30">
                            <h3 className="text-lg font-bold mb-4">Attempted Questions</h3>
                            <div className="grid grid-cols-8 sm:grid-cols-4 gap-2">
                                {filteredResults.map((item, index) => {
                                    const isSelected = currentQuestionIndex === index;
                                    const isCorrect =
                                        normalizeAnswer(item.question?.correctAnswer) ===
                                        normalizeAnswer(item.selected_option);

                                    return (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentQuestionIndex(index)}
                                            className={`w-12 h-12 flex items-center justify-center rounded-lg font-bold ${isCorrect
                                                    ? "bg-green-200 text-green-800"
                                                    : item.selected_option
                                                        ? "bg-red-50 text-red-800"
                                                        : "bg-gray-100 text-gray-700"
                                                } ${isSelected ? "ring-2 ring-blue-500" : ""}`}
                                        >
                                            {index + 1}
                                        </button>
                                    );
                                })}
                            </div>
                        </aside>
                    )}
                </div>
            </div>
        </MathJaxContext>
    );
};

export default ResultActivityPage;
